import { stripHTML } from 'lib/utils/helpers/strip-html';
import { DateTime } from 'luxon';
import { State } from './choices/state';
import { SymptomType } from './choices/symptom-type';
import { Diagnosis } from './diagnosis';
import { SymptomLikeVersion } from './symptom-version';

/**
 * Constructor props.
 */
export interface SymptomLikeConstructorOpts {
  id?: number;
  state: State;
  name: string;
  synonyms: string[];
  slug?: string;
  description: string;
  asDiagnosis?: Diagnosis;
  activeVersionId?: number;
  activeVersion?: SymptomLikeVersion;
  totalVersions?: number;
  created?: DateTime;
  modified?: DateTime;
}

/**
 * Base class for all symptom-like types (symptom, finding, anamnesis).
 */
export abstract class SymptomLike {
  public readonly id: number | undefined;
  public state: State;

  public name: string;
  public synonyms: string[];
  public slug?: string;
  public type: SymptomType;
  public description: string;

  private readonly _asDiagnosis?: Diagnosis;

  public readonly activeVersionId?: number;
  public readonly activeVersion?: SymptomLikeVersion;

  public readonly totalVersions?: number;
  public readonly created?: DateTime;
  public readonly modified?: DateTime;

  constructor(opts: SymptomLikeConstructorOpts & { type: SymptomType }) {
    this.id = opts.id;
    this.state = opts.state;
    this.name = opts.name;
    this.synonyms = opts.synonyms;
    this.slug = opts.slug;
    this.type = opts.type;
    this.description = opts.description;
    this._asDiagnosis = opts.asDiagnosis;
    this.activeVersionId = opts.activeVersionId;
    this.activeVersion = opts.activeVersion;
    this.totalVersions = opts.totalVersions;
    this.created = opts.created;
    this.modified = opts.modified;
  }

  get synonymsString(): string {
    return this.synonyms.join(', ');
  }

  descriptionText(): string {
    return stripHTML(this.description);
  }

  isLinked(): boolean {
    return this._asDiagnosis !== undefined;
  }

  get asDiagnosis(): Diagnosis {
    if (this._asDiagnosis === undefined) {
      throw new Error('Not linked');
    }

    return this._asDiagnosis;
  }

  isSymptom() {
    return this.type === SymptomType.SYMPTOM;
  }

  isFinding() {
    return this.type === SymptomType.FINDING;
  }

  isAnamnesis() {
    return this.type === SymptomType.ANAMNESIS;
  }

  get lastModified(): DateTime | undefined {
    return this.modified || this.created;
  }

  toJson(): any {
    return {
      id: this.id,
      state: this.state.tag,
      name: this.name,
      synonyms: this.synonyms || [],
      description: this.description || '',
      kind: this.type.key
    };
  }

  static fromJson(json: any): SymptomLike {
    const type = SymptomType.fromKey(json.kind);

    const opts: SymptomLikeConstructorOpts = {
      id: json.id,
      state: State.fromTag(json.state),
      name: json.name,
      synonyms: json.synonyms || [],
      slug: json.slug,
      description: json.description || '',
      asDiagnosis: json.diagnosis ? Diagnosis.fromJson(json.diagnosis) : undefined,
      activeVersionId: json.active_version_id || undefined,
      activeVersion: json.active_version
        ? SymptomLikeVersion.fromJson(json.active_version)
        : undefined,
      totalVersions: json.total_versions || undefined,
      created: json.date_created ? DateTime.fromISO(json.date_created) : undefined,
      modified: json.date_modified ? DateTime.fromISO(json.date_modified) : undefined
    };

    switch (type) {
      case SymptomType.SYMPTOM:
        return new Symptom(opts);
      case SymptomType.FINDING:
        return new Finding(opts);
      case SymptomType.ANAMNESIS:
        return new Anamnesis(opts);
      default:
        throw new Error('Unknown symptom type: ' + type);
    }
  }
}

/**
 * A symptom.
 */
export class Symptom extends SymptomLike {
  constructor(opts: SymptomLikeConstructorOpts) {
    super({ ...opts, type: SymptomType.SYMPTOM });
  }

  toString() {
    return (
      '<Symptom: ' +
      (this.id || 0) +
      ", name='" +
      this.name +
      "' state=" +
      this.state +
      '>'
    );
  }
}

/**
 * A clinical finding.
 */
export class Finding extends SymptomLike {
  constructor(opts: SymptomLikeConstructorOpts) {
    super({ ...opts, type: SymptomType.FINDING });
  }

  toString() {
    return (
      '<Finding: ' +
      (this.id || 0) +
      ", name='" +
      this.name +
      "' state=" +
      this.state +
      '>'
    );
  }
}

/**
 * An account on a patient's medical history (anamnesis).
 */
export class Anamnesis extends SymptomLike {
  constructor(opts: SymptomLikeConstructorOpts) {
    super({ ...opts, type: SymptomType.ANAMNESIS });
  }

  toString() {
    return (
      '<Anamnesis: ' +
      (this.id || 0) +
      ", name='" +
      this.name +
      "' state=" +
      this.state +
      '>'
    );
  }
}
